/**
 * @lines98/client — Vite entry point (T-5 + T-6 client integration).
 *
 * Bootstraps the app: mounts the active screen into #app. URL params
 * (`?seed=<uint32>`) are parsed by `app.ts`. Path-based routing для
 * OAuth callback (T-6): if pathname matches `/oauth/<provider>/callback`,
 * dispatches to oauth-callback-screen instead of the game.
 *
 * Per ADR-0001 §A2: vanilla TS, no React/Vue/Phaser. The Zustand store drives
 * reactivity; DOM construction is direct. No router framework — simple pathname
 * check на boot is sufficient (only one non-game path).
 *
 * Identity boot (T-6 client):
 *   - On boot (BOTH game route AND callback route), call `bootIdentity()` —
 *     loads existing identity from IndexedDB или mints new anonymous one via
 *     POST /auth/anonymous. Subsequent fetches attach Bearer header автоматически.
 *
 * CSS is imported here so Vite emits a single bundle (one HTTP roundtrip on
 * cold load — bundle target < 200 KB gzipped per CONSTRAINTS).
 */

import './styles/global.css';
import './styles/postament.css';
import './styles/press-feedback.css'; // переиспользуемый примитив depress-реакции на нажатие
// attract-беат (триггерный пульс CTA) — co-located CSS импортируется самим модулем
// lib/attract-beat.ts (co-located ./attract-beat.css), отдельного import тут не нужно.
import './styles/board.css';
import './styles/daily.css';
import './styles/result.css';
import './styles/replay.css';
import './components/data-table.css';
import './styles/leaderboard.css';
import './styles/oauth-callback.css';

import { mountApp, rerenderActivePhaseForLocale } from './app.js';
import { installAssetProtection } from './lib/asset-protect.js';
import { mountBgLayer } from './lib/bg-layer.js';
// [ВРЕМЕННО — снять после починки realme] импорт ради захвата ?vfitdebug на boot,
// ДО зачистки URL приложением (см. dev-flags).
import './lib/dev-flags.js';
import { emitIdentityChanged } from './lib/identity-events.js';
import { bootInitialLocale, syncLocaleOnLogin } from './lib/i18n/index.js';
import { canonicalEnRedirect, parseContentPath } from '@lines98/shared/config/content-routes';
import { UI_LANGS } from '@lines98/shared/config/seo';
import { initTheme } from './lib/theme.js';
import { installViewportSizer } from './lib/viewport-sizer.js';
import { mountOAuthCallbackScreen } from './screens/oauth-callback-screen.js';
import { prefetchDailySeed } from './services/daily-api.js';
import { prefetchDailyStatus } from './services/daily-status-prefetch.js';
import { bootIdentity } from './services/identity.js';
import { prefetchLeaderboard } from './services/leaderboard-prefetch.js';
import { prefetchMetaState } from './services/meta-prefetch.js';
import {
  extractProviderFromPath,
  isOAuthCallbackRoute,
} from './services/oauth-callback.js';

const host = document.getElementById('app');
if (!host) {
  throw new Error('#app host not found in index.html — boot aborted');
}

// Общий painted фон-слой (ствол, ОДИН на приложение — lib/bg-layer.ts). Монтируем ПЕРВЫМ,
// до любого mount экрана и до reveal-gate стартовой: слой (position:fixed; inset:0;
// z-index:-1; var(--bg-grad)) покрывает вьюпорт уже на первом кадре → нет white-flash на
// load/переходах И нет mobile-webkit repaint-«белой полосы» при momentum-скролле (заменил
// background-attachment:fixed на body, тикет 2026-06-24 AC-1..AC-4/AC-7). Идемпотентно;
// покрывает ОБА route'а (oauth-callback и game). Чистый CSS-узел — анти-чит/логику не трогает.
mountBgLayer(host);

/**
 * Снять locale reveal-gate (П5): pre-paint inline (index.html) гейтит #app (opacity:0) пока
 * каталог выбранного НЕ-en языка не готов, чтобы не мелькнуть en-кадром (FOUC). Снимаем по
 * РЕЗОЛВУ `bootInitialLocale` (каталог загружен + store-locale выставлен → первый ВИДИМЫЙ
 * кадр уже на нужном языке). Идемпотентно; вызывается в `.finally` (даже при ошибке резолва —
 * гейт не должен висеть вечно, AC-5.3). Если гейта нет (en / без выбора) — no-op.
 */
function clearLocaleGate(): void {
  try {
    document.documentElement.removeAttribute('data-locale-gate');
  } catch {
    /* defensive — без throw */
  }
}

/**
 * Язык DEEP-LINK URL'а для boot-резолва языка (fix/i18n-unify-homepage): прямой заход на
 * `/ru/`(-статью) или `?demo=…&lang=ru` (baseline) — авторитетный сигнал языка (сильнее
 * браузера, слабее явного персиста). Без него `bootInitialLocale` резолвил бы язык
 * браузера (en) и перетёр бы deep-link `/ru/` → главная флипалась на en (баг до unify).
 * Источник:
 *   1) `?lang=<UI_LANG>` (demo/baseline-route) — если задан И валиден;
 *   2) язык пути модели B (`/ru/…` → ru; `/en/…`-канон → en; голый en-корень `/` → en).
 * Возвращает код или null (нейтрально — резолвер фолбэкнет на браузер/en).
 */
function bootUrlLang(): string | null {
  if (typeof window === 'undefined') return null;
  const demo = new URLSearchParams(window.location.search).get('lang');
  if (demo && (UI_LANGS as readonly string[]).includes(demo)) return demo;
  const p = window.location.pathname;
  // `/en/…` канонизируется на корень-en — язык всё равно en.
  const enCanon = canonicalEnRedirect(p);
  return parseContentPath(enCanon ?? p).lang;
}

// Cross-browser foundation (prototype): own --cell-size / --m from a measured
// container instead of fragile 100vw CSS math. Install before first mount so the
// first paint already has the deterministic value (no FOUC).
installViewportSizer();

// Лёгкая защита игровых PNG/арт-ассетов от казуального скачивания (fe-png-protect,
// Вариант 1). ОДИН делегированный document-перехват contextmenu/dragstart на ВСЁ
// приложение (покрывает все динамически смонтированные экраны) + первичная простановка
// draggable=false. Не глушит игровой ввод (AC-2: pointer-events не трогаем). Ставится
// ДО первого mount, чтобы право-клик/drag отсекались с первого кадра. Идемпотентно.
installAssetProtection();

// Применить сохранённую тему приложения (light `retro` / dark) к documentElement
// (§5.5). Идемпотентно с head-сниппетом (index.html) — тот ставит тему ДО первой
// отрисовки (без вспышки), этот — единый рантайм-источник на boot.
initTheme();

// Path-based routing: OAuth callback route or game route.
const pathname = window.location.pathname;
if (isOAuthCallbackRoute(pathname)) {
  const provider = extractProviderFromPath(pathname);
  if (provider) {
    // Identity boot is required для callback (Bearer attached to POST /callback).
    // bootIdentity() returns immediately если already loaded from IndexedDB;
    // если absent, mints anonymous identity (no Bearer needed для that endpoint).
    // Язык-каталог тоже грузим ДО монтирования: экран callback'а резолвит ВСЕ
    // видимые строки через `t('auth.oauth.*')` (fe-overlays П2 i18n) — без
    // загруженного каталога `t()` отдал бы голый ключ. Этот route уходит ДО
    // основной game-route ветки (где грузится `bootInitialLocale`), поэтому boot
    // языка нужен и здесь. Параллелим с identity, ждём оба перед mount.
    void Promise.all([bootIdentity(), bootInitialLocale(bootUrlLang())])
      .finally(clearLocaleGate)
      .then(() => {
        mountOAuthCallbackScreen({
          host,
          provider,
          searchString: window.location.search,
        });
      });
  } else {
    // Fallthrough — shouldn't happen given isOAuthCallbackRoute guard, defensive.
    mountApp(host);
  }
} else {
  // Standard game route. Boot identity in background — game UI doesn't block
  // on it (T-7 will start gating /finish-game на Bearer). Once identity is ready
  // (Bearer minted/loaded), warm the per-player snapshots in the background so the
  // FIRST visit to the Мастерская / лидерборду paints from a fresh snapshot, not a
  // cold network wait (owner-playtest fix 2026-06-10).
  //
  // Both meta-state AND leaderboard snapshots are keyed by playerId (per-player
  // isolation, instant-ui-hydration §3) — their `save*` is a no-op until identity
  // is resolved. So both prefetches MUST run AFTER bootIdentity resolves a real
  // identity, otherwise the fetched page lands before playerId exists and the
  // snapshot write is silently swallowed → first visit stays cold (owner-playtest
  // 2026-06-10: лидерборд первый заход «идёт загрузка», т.к. префетч стартовал ДО
  // identity → saveLeaderboardPane no-op → снимок пуст). Guest / mint failure →
  // null → skip (the screens then show their loading / «Войди» path).
  // Fire-and-forget through the prefetch modules — never blocks boot/render.
  // Гео-дефолт + персист языка первого захода (ADR-0021 D-3/D-4, T-I18N-3): резолв
  // «явный localStorage > гео (navigator.language) > en» → подгрузка каталога +
  // store.locale (рейл-триггер + контент перерисуются реактивно). Fire-and-forget —
  // boot не ждёт каталог; до его готовности активен en-дефолт стора. SEO-дефолт en
  // (canonical краулера) этим НЕ затрагивается — это язык ЖИВОГО юзера (D-3).
  // Снять locale reveal-gate по готовности каталога (П5): первый ВИДИМЫЙ кадр уже на нужном
  // языке (нет FOUC en→ru). .finally — даже при ошибке резолва гейт снимается (AC-5.3).
  // ПО РЕЗОЛВУ каталога — ре-рендер активной ИГРОВОЙ фазы (owner-bug 2026-06-24, root-cause
  // «b»: F5 на `/book`). На cold-boot `mountApp` (ниже, синхронно) монтирует фазу ДО
  // готовности lazy-каталога → первый кадр на сырых ключах. Если резолв-язык совпал со
  // store-дефолтом (`en`), store.setLocale не сменил значение → подписка app.ts не
  // ре-монтировала фазу. Поэтому форсим re-mount активной игровой фазы здесь, по готовности
  // каталога — лейблы/список рисуются заново на верном языке. `.then` ВЫПОЛНИТСЯ ПОСЛЕ
  // синхронного `mountApp` (фаза уже смонтирована → re-mount валиден). No-op до mountApp
  // (runtime пуст) — порядок гарантирован микротаской после синхронного хвоста ветки.
  void bootInitialLocale(bootUrlLang())
    .then(rerenderActivePhaseForLocale)
    .finally(clearLocaleGate);

  void bootIdentity().then((identity) => {
    // Reconcile the account-slot (and any open Профиль) to the JUST-RESOLVED identity.
    // The slot is mounted SYNCHRONOUSLY by `mountApp` below from `navRailAccountIdentity()`,
    // which reads `getCurrent()` — but on a cold load that read returns null because boot
    // is still async (load-from-IndexedDB / anon mint). Without this emit a RETURNING
    // LOGGED-IN player would keep the guest «Войти» pill forever (account-profile-redesign
    // §10 instant-hydration / AC-3 violation). We reuse the single identity-events seam
    // (same signal login/logout fire) — `mountApp`'s `onIdentityChanged` subscriber then
    // re-renders the slot to a chip for an authed identity. For a guest (anon mint,
    // authMethod=null) `navRailAccountIdentity()` returns null → the slot re-renders to the
    // SAME «Войти» pill (no regression). Fire on null too (degraded boot) — harmless reconcile.
    emitIdentityChanged();
    if (identity) {
      // Sync-при-логине языка (ADR-0021 D-4a): если это АВТОРИЗОВАННАЯ identity
      // (вошёл email/OAuth, не аноним) — слить выбор «последний явный человека
      // побеждает»: гостевой localStorage → пишется на аккаунт; иначе серверный
      // `players.locale` → применяется. Best-effort (язык косметика). Аноним —
      // no-op (его выбор живёт в localStorage, серверного персиста нет).
      void syncLocaleOnLogin();

      prefetchMetaState();
      // Прогреть стартовую страницу лидерборда (Доска дня → Сегодня — дефолтная
      // посадка экрана): к первому заходу строки обычно уже прилетели → лидерборд
      // рисует их из свежего снимка, без «Загружаем…» (instant-ui-hydration §4).
      // Тот же легальный публичный GET `/daily/leaderboard`, просто раньше во
      // времени; снимок keyed по playerId — потому ТОЛЬКО после готовности identity
      // (иначе saveLeaderboardPane проглатывается). Дедуп с mount-fetch'ем экрана
      // через shared-flight (один запрос, не дубль). Best-effort — ошибка/offline
      // внутри модуля проглатывается и не ломает boot.
      prefetchLeaderboard();

      // Прогреть СТАТУС доски дня (idle / done) пока игрок на пикере «Играть»
      // (новый дефолтный лендинг, fix/play-as-start-page). Played-снимок / idle-маркер
      // keyed по playerId — потому ТОЛЬКО после готовности identity (иначе write
      // проглатывается → первый заход остаётся холодным). К первому заходу на доску дня
      // снимок уже лёг → она рисует idle/done СИНХРОННО, без нейтрального loading-кадра
      // (instant-ui-hydration §4). Переиспользует тёплый seed-кэш (prefetchDailySeed
      // выше) + IDB-канон daily-cache; mount-boot экрана — идемпотентный reconcile.
      // Best-effort — ошибка/offline проглатывается внутри модуля и не ломает boot.
      prefetchDailyStatus();
    }
  });

  // Prefetch the daily seed in the background to warm the in-memory seed-cache
  // (daily-api.ts §3A.1 note). By the time the player navigates to the daily
  // board (the default boot phase) the cache is warm → daily-board-screen paints
  // `renderIdle` immediately, with no loading flash on the FIRST visit. This is
  // the same legal `GET /daily/seed` with the same anti-cheat guardrails — just
  // earlier in time. Daily seed is public (no Bearer needed) and its cache is NOT
  // keyed by playerId, so it stays here — outside the identity gate. Fully
  // fire-and-forget: a prefetch error (offline) is swallowed and must NOT break
  // boot; the daily screen still does its own fetch + error-state on a cache miss.
  prefetchDailySeed();

  mountApp(host);
}
